import type {RoomType} from '@/modules/common/models/room';

import {Room} from '@/modules/common/models/room';

export type RoomsType = Iterable<RoomType> & {
  id: string;
  adultsCount: number;
  childrenCount: number;
  travellerCount: number;
  childrenAges: Array<number>;
  length: number;
};

export const Rooms = {
  create(...rooms: Array<RoomType>): RoomsType {
    // these fields are way more often get than set, so we pre-compute on creation
    const adultsCount = rooms.reduce(
      (result, room) => result + room.adultsCount,
      0
    );
    const childrenCount = rooms.reduce(
      (result, room) => result + room.childrenAges.length,
      0
    );
    const childrenAges = rooms.reduce(
      (result, room) => result.concat(room.childrenAges),
      [] as Array<number>
    );
    const travellerCount = adultsCount + childrenCount;

    const toString = () =>
      rooms
        .map((room) => room.toString())
        .filter((roomString) => roomString.length > 0)
        .join('_');

    // using defineProperties here because we don't want methods to show up in iteration
    // @ts-expect-error TS doesn't "see" Symbol.iterator for some reason 🤷‍♂️
    return Object.defineProperties(
      {
        // When passing a "Rooms"-object to something like lodash.isEqual,
        // it will treat it as a regular object and compare via its properties (including our computed properties).
        //
        // But lets say we do something like this:
        //   isEqual(Rooms.fromString('A,A_A'), Rooms.fromString('A_A,A'))
        //
        // Without anything encoding the order/distribution of rooms, the rooms above would be considered "equal".
        //  Rooms.fromString('A,A_A') -> {adultsCount: 3, childrenCount: 0, travellerCount: 3, childrenAges: [], length: 3}
        //  Rooms.fromString('A_A,A') -> {adultsCount: 3, childrenCount: 0, travellerCount: 3, childrenAges: [], length: 3}
        //
        // So we need to add something, to uniquely identify the rooms.
        // The string representation is just that.
        get id() {
          return toString();
        },
        get adultsCount() {
          return adultsCount;
        },
        get childrenCount() {
          return childrenCount;
        },
        get travellerCount() {
          return travellerCount;
        },
        get childrenAges() {
          return childrenAges;
        },
        get length() {
          return rooms.length;
        },
      },
      {
        toString: {
          value() {
            return toString();
          },
        },
        [Symbol.iterator]: {
          value() {
            return rooms.values();
          },
        },
      }
    );
  },
  fromString(roomsString: string): RoomsType {
    const rooms = roomsString
      .trim()
      .split('_')
      .map((v) => Room.fromString(v.trim()));

    return Rooms.create(...rooms);
  },
};
